import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthenticationModule } from './features/auth/authentication/authentication.module';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import { StoreModule } from '@ngrx/store';
import { reducers } from './core/app.state';
import { AuthEffects } from './features/auth/authentication/store/auth.effects';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { APP_VERSION, environment } from 'src/environments/environment';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LayoutModule } from './layout/layout.module';
import { ProduitsModule } from './features/produits/produits.module';
import { SharedModule } from './shared/shared.module';
import { ProductsEffects } from './features/produits/store/produits.effects';
import { TokenInterceptor } from './core/interceptors/token.interceptor';
import { CategoriesEffects } from './features/categories/store/categories.effects';
import { CategoriesModule } from './features/categories/categories.module';
import { TagsModule } from './features/tags/tags.module';
import { TagsEffects } from './features/tags/store/tags.effects';
import { BrandsModule } from './features/brands/brands.module';
import { BrandsEffects } from './features/brands/store/brands.effects';
import { CustomersModule } from './features/customers/customers.module';
import { CustomersEffects } from './features/customers/store/customers.effects';
import { CatalogsModule } from './features/catalogs/catalogs.module';
import { CatalogsEffects } from './features/catalogs/store/catalogs.effects';
import { CompaniesGroupsModule } from './features/companies-groups/companies-groups.module';
import { CompaniesGroupsEffects } from './features/companies-groups/store/companies-groups.effects';
import { QuotationsModule } from './features/quotations/quotations.module';
import { QuotesEffects } from './features/quotations/store/quotations.effects';
import { OrdersModule } from './features/orders/orders.module';
import { OrdersEffects } from './features/orders/store/orders.effects';
import { ImportsModule } from './features/imports/imports.module';
import { OrganizeModule } from './features/organize/organize.module';
import { ImportProductsEffects } from './features/imports/store/imports.effects';
import { SegmentsModule } from './features/segments/segments.module';
import { SegmentsEffects } from './features/segments/store/segments.effects';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { SettingsModule } from './features/settings/settings.module';
import { SettingsEffects } from './features/settings/store/settings.effects';
import { CustomMatPaginatorIntl } from './shared/custom-mat-paginator-intl.service';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { ComparatorModule } from './features/comparator/comparator.module';
import { ComparatorEffects } from './features/comparator/store/comparator.effects';
import { SuppliersEffects } from './features/suppliers/store/suppliers.effects';
import { SuppliersModule } from './features/suppliers/suppliers.module';
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import { HttpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { ToasterModule } from './shared/toaster/toaster.module';
import { RuntimeConfigService } from './config/runtime-config.service';

import { AgentsModule } from './features/agents/agents.module';
import { AgentsEffects } from './features/agents/store/agents.effects';
import { UiModule } from './ui/ui.module';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule } from '@angular/material-moment-adapter';
import { WarehousesEffects } from './features/warehouses/store/warehouses.effects';
import { WarehosesModule } from './features/warehouses/warehoses.module';
import { PricebooksEffects } from './features/pricebook/store/pricebook.effects';
import { SmartInboxEffects } from './features/smart-inbox/store/smart-inbox.effects';
import { AiChatEffects } from './features/ai-chat/store/ai-chat.effects';
import { KnowledgeBaseEffects } from './features/knowledge-base/store/knowledge-base.effects';
import { AliasesEffects } from './features/knowledge-base/store/aliases.effects';
import { ExternalReferencesEffects } from './features/knowledge-base/store/external-references.effects';
import { IgnoreCodesEffects } from './features/knowledge-base/store/ignore-codes.effects';
import { PreferredProductsEffects } from './features/knowledge-base/store/preferred-products.effects';
import { CustomerEmailsEffects } from './features/knowledge-base/store/customer-emails.effects';
import { LocationEmailsEffects } from './features/knowledge-base/store/location-emails.effects';
import { NotificationEffects } from './features/notifications/store/notification.effects';
import { DesktopNotificationEffects } from './features/notifications/store/desktop-notification.effects';
import { PermissionsEffects } from './core/store/permissions/permissions.effects';
import { FieldVisibilityEffects } from './core/store/field-visibility/field-visibility.effects';
import { WorkflowsModule } from './features/workflows/workflows.module';
import { WorkflowsEffects } from './features/workflows/store/workflows.effects';
import { LucideAngularModule } from 'lucide-angular';
import { VOLTA_ICONS } from './ui/icons/volta-icons';

// Angular ships `en` data only; `formatDate`/`DatePipe` THROW NG0701 on any other
// locale that was never registered. One per supported UI language (LanguageService).
registerLocaleData(localeEn);
registerLocaleData(localeFr);
registerLocaleData(localeIt);

export function httpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    AuthenticationModule,
    BrowserAnimationsModule,
    HttpClientModule,
    SharedModule,
    UiModule,
    LayoutModule,
    ProduitsModule,
    CategoriesModule,
    BrandsModule,
    OrganizeModule,
    HttpClientModule,
    CustomersModule,
    CatalogsModule,
    TagsModule,
    CompaniesGroupsModule,
    QuotationsModule,
    OrdersModule,
    ImportsModule,
    ComparatorModule,
    SegmentsModule,
    SettingsModule,
    SuppliersModule,
    AgentsModule,
    WorkflowsModule,
    ToasterModule,
    MatMomentDateModule,
    WarehosesModule,
    // Registers the Volta icon set in the root injector so <ui-icon> can resolve
    // names anywhere in the app. See src/app/ui/icons/volta-icons.ts.
    LucideAngularModule.pick(VOLTA_ICONS),
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot([
      AuthEffects,
      ProductsEffects,
      CategoriesEffects,
      TagsEffects,
      BrandsEffects,
      CustomersEffects,
      CatalogsEffects,
      CompaniesGroupsEffects,
      QuotesEffects,
      OrdersEffects,
      ImportProductsEffects,
      SegmentsEffects,
      SettingsEffects,
      ComparatorEffects,
      SuppliersEffects,
      AgentsEffects,
      WarehousesEffects,
      PricebooksEffects,
      SmartInboxEffects,
      AiChatEffects,
      KnowledgeBaseEffects,
      AliasesEffects,
      ExternalReferencesEffects,
      IgnoreCodesEffects,
      PreferredProductsEffects,
      CustomerEmailsEffects,
      LocationEmailsEffects,
      NotificationEffects,
      DesktopNotificationEffects,
      PermissionsEffects,
      FieldVisibilityEffects,
      WorkflowsEffects,
    ]),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (rcs: RuntimeConfigService) => () => rcs.load(),
      deps: [RuntimeConfigService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: MatPaginatorIntl,
      useClass: CustomMatPaginatorIntl,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        hasBackdrop: true,
        disableClose: true,
        scrollStrategy: new NoopScrollStrategy(),
      },
    },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {

  constructor() { this.handleNewVersion(); }

  handleNewVersion() {
    const currentVersion = localStorage.getItem('app_version'),
      isNewVersion = currentVersion !== APP_VERSION;
  
    if (isNewVersion) {
      localStorage.setItem('app_version', APP_VERSION);
      location.reload();
    }
    console.log(`App Version: ${APP_VERSION}`);
  }
}
